import React, { useState } from 'react';
import { Calendar } from 'react-date-range';
import MetRateDownloadButtons from './MetRateDownloadButtons';
import SensorSelector from './SensorSelector';
import Modal from '../Modal/Modal';
import { getStoredRoles, isFodRole } from '../../helpers/auth';

import {
  constructUTCDate,
  getISODateString,
} from '../../helpers/datetime';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './MetRateDownloadView.css';

const SENSOR_TABLES = [
  { key: 'ecsSuitRequest', title: 'ECS Suit', sensorKey: 'ecs-suit' },
  { key: 'metRateRequest', title: 'Met Rate', sensorKey: 'met-rate' },
  { key: 'heartRateRequest', title: 'Heart Rate', sensorKey: 'heart-rate' },
  { key: 'humidTempRequest', title: 'Humidity Temp', sensorKey: 'humid-temp' },
];

// The ECS table's evNum field actually carries the umbilical_color rather than
// a true EV number, so it is never filtered by crew member.
const CREW_FILTERED_KEYS = ['metRateRequest', 'heartRateRequest', 'humidTempRequest'];

// Crew members are always selectable, regardless of what the data index
// contains for the chosen date and environment.
const SELECTABLE_CREW_MEMBERS = ['EV1', 'EV2'];

// Resolve the most recent date that actually has data for a location so the
// view never lands on an empty day (e.g. "today" with no runs yet), which would
// otherwise make every crew member read "no data" regardless of availability.
function getLatestAvailableDate(dataIndexes, location) {
  const locationDataIndexes = (dataIndexes || {})[location.toUpperCase()] || {};
  const dates = Object.keys(locationDataIndexes).sort();
  return dates.length ? dates[dates.length - 1] : null;
}

function getDefaultDataRequest(dataIndexes, date, location, table) {
  let defaultDataRequest;
  const locationsDataIndexes = (dataIndexes || {})[location.toUpperCase()];
  const daysDataIndexes = (locationsDataIndexes || {})[date];
  const imagesDataIndexes = (daysDataIndexes || {})[table];
  Object.keys(imagesDataIndexes || {}).forEach((thingName) => {
    if (!defaultDataRequest) {
      defaultDataRequest = imagesDataIndexes[thingName];
    }
  });
  return defaultDataRequest || {};
}

function normalizeCrewMember(evNum) {
  if (!evNum && evNum !== 0) return null;
  const match = `${evNum}`.match(/(\d+)/);
  return match ? `EV${match[1]}` : `${evNum}`;
}

function sensorsForCrewMember(sensors, crewMember, filterByCrew) {
  if (!sensors) return {};
  if (!crewMember || !filterByCrew) return sensors;
  return Object.keys(sensors).reduce((filtered, thingName) => {
    if (normalizeCrewMember((sensors[thingName] || {}).evNum) === crewMember) {
      return { ...filtered, [thingName]: sensors[thingName] };
    }
    return filtered;
  }, {});
}

function firstSensorForCrewMember(sensors, crewMember, filterByCrew) {
  const filtered = sensorsForCrewMember(sensors, crewMember, filterByCrew);
  const [firstThingName] = Object.keys(filtered);
  return firstThingName ? filtered[firstThingName] : undefined;
}

function getDefaultRequestForCrewMember(dataIndexes, date, location, table, crewMember, filterByCrew) {
  const sensors = (((dataIndexes || {})[location.toUpperCase()] || {})[date] || {})[table];
  return firstSensorForCrewMember(sensors, crewMember, filterByCrew)
    || getDefaultDataRequest(dataIndexes, date, location, table);
}

export default function MetRateDownloadView(props) {
  const { dataIndexes, jwt } = props || {};
  const defaultLocation = 'nbl';
  const fallbackDate = getISODateString(new Date());
  // Prefer the latest day that has data for the location; only fall back to
  // today when the location has no data at all.
  const resolveDefaultDate = (loc) => getLatestAvailableDate(dataIndexes, loc) || fallbackDate;
  const defaultDate = resolveDefaultDate(defaultLocation);

  const userRoles = getStoredRoles();
  const isFod = isFodRole(userRoles);
  const availableLocations = isFod ? ['nbl'] : ['nbl', 'argos', 'fieldtest', 'uploads'];

  const defaultSelectedFiles = {
    ecsSuitRequest: true,
    metRateRequest: true,
    heartRateRequest: true,
    humidTempRequest: true,
  };

  const [downloadLinks, setDownloadLinks] = useState({});
  const [crewMember, setCrewMember] = useState(null);
  const [isFilesModalShowing, setIsFilesModalShowing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(defaultSelectedFiles);
  const [parameters, setParameters] = useState({
    date: defaultDate,
    location: defaultLocation,
    ecsSuitRequest: getDefaultDataRequest(dataIndexes, defaultDate, defaultLocation, 'ecs_suit_data'),
    metRateRequest: getDefaultDataRequest(dataIndexes, defaultDate, defaultLocation, 'iot_hhp_model'),
    heartRateRequest: getDefaultDataRequest(dataIndexes, defaultDate, defaultLocation, 'iot_heart_rate'),
    humidTempRequest: getDefaultDataRequest(dataIndexes, defaultDate, defaultLocation, 'iot_humidity_temperature'),
  });

  const {
    date,
    location,
    ecsSuitRequest,
    metRateRequest,
    heartRateRequest,
    humidTempRequest,
  } = parameters || {};

  const selectedDataIndexes = dataIndexes[location.toUpperCase()];
  const availableDates = Object.keys(selectedDataIndexes || {}).sort() || [];
  const [minAvailableDate] = availableDates || [];
  const maxAvailableDate = availableDates.slice(-1).pop();

  const selectedDataIndex = (selectedDataIndexes || {})[date];
  const {
    ecs_suit_data: ecsSensors,
    iot_hhp_model: metRateSensors,
    iot_heart_rate: heartRateSensors,
    iot_humidity_temperature: humidTempSensors,
    undefined: uploaders = {},
  } = selectedDataIndex || {};

  // Heart Rate image is no longer offered on the NBL environment.
  const isHeartRateHidden = location === 'nbl';

  // Crew members are always selectable, regardless of what the data index
  // contains for the current date/environment.
  const crewMembers = SELECTABLE_CREW_MEMBERS;
  const activeCrewMember = crewMember;

  const applyCrewMember = (nextCrewMember, nextDate = date, nextLocation = location) => {
    setDownloadLinks({});
    setSelectedFiles(defaultSelectedFiles);
    setIsFilesModalShowing(false);
    setCrewMember(nextCrewMember);
    setParameters({
      date: nextDate,
      location: nextLocation,
      ecsSuitRequest: getDefaultRequestForCrewMember(dataIndexes, nextDate, nextLocation, 'ecs_suit_data', nextCrewMember, false),
      metRateRequest: getDefaultRequestForCrewMember(dataIndexes, nextDate, nextLocation, 'iot_hhp_model', nextCrewMember, true),
      heartRateRequest: getDefaultRequestForCrewMember(dataIndexes, nextDate, nextLocation, 'iot_heart_rate', nextCrewMember, true),
      humidTempRequest: getDefaultRequestForCrewMember(dataIndexes, nextDate, nextLocation, 'iot_humidity_temperature', nextCrewMember, true),
    });
  };

  const sensorsByTable = {
    ecsSuitRequest: ecsSensors,
    metRateRequest: metRateSensors,
    heartRateRequest: heartRateSensors,
    humidTempRequest: humidTempSensors,
  };

  // Single source of truth: the sensor files available to download for a given
  // crew member. Both the crew-member availability flag and the modal list use
  // this so the advertised count can never differ from what is selectable.
  const availableSensorConfigsFor = (member) => SENSOR_TABLES.filter(({ key }) => {
    if (key === 'heartRateRequest' && isHeartRateHidden) return false;
    const filtered = sensorsForCrewMember(
      sensorsByTable[key],
      member,
      CREW_FILTERED_KEYS.includes(key),
    );
    return Object.keys(filtered).length > 0;
  });

  const visibleSensorConfigs = availableSensorConfigsFor(activeCrewMember);

  // Umbilical-change edge case: during a run the crew switches from one ECS
  // umbilical to another (umbilical_color arrives as the ECS evNum). All runs
  // are merged into one file, with the aborted run labeled below the rest.
  const ecsUmbilicals = Object.keys(ecsSensors || {})
    .map((thingName) => (ecsSensors[thingName] || {}).evNum)
    .filter((evNum) => evNum !== undefined && evNum !== null && evNum !== '');
  const hasUmbilicalChange = !!activeCrewMember && new Set(ecsUmbilicals).size > 1;

  const toggleFileSelection = (key) => setSelectedFiles((prev) => ({
    ...prev,
    [key]: !prev[key],
  }));

  // A crew member (EV1/EV2) must be selected AND have at least one file
  // available for the selected date/environment before data can be requested.
  // When the "no data" state is the correct one, the request button stays
  // disabled rather than opening an empty modal.
  const hasAvailableFiles = visibleSensorConfigs.length > 0;
  const canRequest = !!activeCrewMember && hasAvailableFiles;

  const filesModalContent = (
    <div className="download-files-modal">
      <p className="download-files-modal-subtitle">
        {`Select which files to download for ${activeCrewMember} on ${date} (${location.toUpperCase()}), then confirm your request.`}
      </p>
      {visibleSensorConfigs.length > 0 ? (
        visibleSensorConfigs.map(({ key, title }) => (
          <div className="file-selection-row" key={key}>
            <label className="file-selection-checkbox" htmlFor={`file-${key}`}>
              <input
                id={`file-${key}`}
                type="checkbox"
                checked={!!selectedFiles[key]}
                onChange={() => toggleFileSelection(key)}
              />
              <SensorSelector
                date={date}
                request={parameters[key]}
                setRequestValue={(value) => setParameters({ ...parameters, [key]: value })}
                title={title}
              />
            </label>
          </div>
        ))
      ) : (
        <p className="download-files-modal-empty">
          No files are available for this crew member on the selected date.
        </p>
      )}
      {
        hasUmbilicalChange ? (
          <p className="umbilical-change-notice">
            {activeCrewMember}
            {' '}
            changed umbilicals during this run. All data will be merged
            into a single file, with the aborted run labeled below the
            rest.
          </p>
        ) : null
      }
      <MetRateDownloadButtons
        jwt={jwt}
        isFod={isFod}
        location={location}
        crewMember={activeCrewMember}
        selectedFiles={selectedFiles}
        hasUmbilicalChange={hasUmbilicalChange}
        metRateSensors={metRateSensors}
        heartRateSensors={heartRateSensors}
        downloadLinks={downloadLinks}
        setDownloadLinks={setDownloadLinks}
        metRateRequest={selectedFiles.metRateRequest ? metRateRequest : {}}
        ecsSuitRequest={selectedFiles.ecsSuitRequest ? ecsSuitRequest : {}}
        heartRateRequest={(selectedFiles.heartRateRequest && !isHeartRateHidden) ? heartRateRequest : {}}
        humidTempRequest={selectedFiles.humidTempRequest ? humidTempRequest : {}}
        uploadRequest={uploaders['hippo-uploader']}
      />
    </div>
  );

  return (
    <div className="met-rate-download-view">
      <div className="download-card">
        <div className="download-step">
          <div className="download-step-header">
            <span className="download-step-number">1</span>
            <span className="download-step-label">Environment</span>
          </div>
          <select
            className="download-view-selector"
            defaultValue="nbl"
            onChange={
              ({ target: { value } }) => applyCrewMember(null, resolveDefaultDate(value), value)
            }
          >
            {availableLocations.map((val) => <option key={val} value={val}>{val.toUpperCase()}</option>)}
          </select>
        </div>

        <div className="download-step">
          <div className="download-step-header">
            <span className="download-step-number">2</span>
            <span className="download-step-label">Crew member</span>
          </div>
          <div className="crew-member-toggle">
            {crewMembers.map((member) => {
              const fileCount = availableSensorConfigsFor(member).length;
              return (
                <button
                  type="button"
                  key={member}
                  className={`crew-member-option${activeCrewMember === member ? ' selected' : ''}`}
                  onClick={() => applyCrewMember(activeCrewMember === member ? null : member)}
                >
                  {member}
                  <span className="crew-member-file-count">
                    {fileCount > 0 ? `${fileCount} file${fileCount === 1 ? '' : 's'}` : 'no data'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="download-step">
          <div className="download-step-header">
            <span className="download-step-number">3</span>
            <span className="download-step-label">Date and time</span>
          </div>
          <div className="download-calendar">
            <Calendar
              disabledDay={(day) => availableDates.indexOf(getISODateString(day)) === -1}
              minDate={minAvailableDate ? constructUTCDate(minAvailableDate) : null}
              maxDate={maxAvailableDate ? constructUTCDate(maxAvailableDate) : null}
              onChange={(day) => applyCrewMember(activeCrewMember, getISODateString(day), location)}
              date={constructUTCDate(date)}
            />
            <div className="calendar-legend">
              <span className="calendar-legend-item">
                <span className="calendar-legend-swatch available" />
                Data available
              </span>
              <span className="calendar-legend-item">
                <span className="calendar-legend-swatch selected" />
                Selected
              </span>
            </div>
          </div>
        </div>

        <div className="download-step">
          <button
            type="button"
            className="download-button-primary button download-request-button"
            onClick={() => setIsFilesModalShowing(true)}
            disabled={!canRequest}
          >
            Request Data
          </button>
          <p className="download-request-hint">
            {(() => {
              if (!activeCrewMember) return 'Select a crew member to request data.';
              if (!hasAvailableFiles) {
                return `No files are available for ${activeCrewMember} on ${date} (${location.toUpperCase()}).`;
              }
              return 'Choose which files to download in the next step.';
            })()}
          </p>
        </div>
      </div>

      <Modal
        isShowing={isFilesModalShowing && canRequest}
        hide={() => setIsFilesModalShowing(false)}
        title="Files to download"
        innerComponent={filesModalContent}
      />
    </div>
  );
}
